// Serviço para gerenciar assinaturas no Supabase
import { supabase } from '../lib/supabase';

export interface Assinatura {
  id: string;
  contaId: string;
  pacoteId: string;
  pacoteNome: string;
  status: 'ativa' | 'cancelada' | 'suspensa' | 'vencida';
  dataInicio: string;
  dataFim: string | null;
  valorMensal: number;
  formaPagamento: string | null;
  proximaCobranca: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AssinaturaDB {
  id: string;
  conta_id: string;
  pacote_id: string;
  status: string;
  data_inicio: string;
  data_fim: string | null;
  valor_mensal: number;
  forma_pagamento: string | null;
  proxima_cobranca: string | null;
  created_at: string;
  updated_at: string;
}

function assinaturaDBToAssinatura(assinaturaDB: AssinaturaDB, pacoteNome?: string): Assinatura {
  return {
    id: assinaturaDB.id,
    contaId: assinaturaDB.conta_id,
    pacoteId: assinaturaDB.pacote_id,
    pacoteNome: pacoteNome || '',
    status: assinaturaDB.status as 'ativa' | 'cancelada' | 'suspensa' | 'vencida',
    dataInicio: assinaturaDB.data_inicio,
    dataFim: assinaturaDB.data_fim || null,
    valorMensal: Number(assinaturaDB.valor_mensal),
    formaPagamento: assinaturaDB.forma_pagamento || null,
    proximaCobranca: assinaturaDB.proxima_cobranca || null,
    createdAt: assinaturaDB.created_at,
    updatedAt: assinaturaDB.updated_at,
  };
}

// Buscar todas as assinaturas
export async function getAssinaturas(): Promise<Assinatura[]> {
  try {
    const { data, error } = await (supabase as any)
      .from('assinaturas')
      .select(`
        *,
        pacotes:nome
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Buscar nomes dos pacotes separadamente
    const assinaturasComNomes = await Promise.all(
      (data || []).map(async (assinatura: AssinaturaDB) => {
        const { data: pacote } = await (supabase as any)
          .from('pacotes')
          .select('nome')
          .eq('id', assinatura.pacote_id)
          .single();

        return assinaturaDBToAssinatura(assinatura, pacote?.nome);
      })
    );

    return assinaturasComNomes;
  } catch (error) {
    console.error('[assinaturasService] Erro ao buscar assinaturas:', error);
    return [];
  }
}

// Buscar assinatura por ID
export async function getAssinaturaById(id: string): Promise<Assinatura | null> {
  try {
    const { data, error } = await (supabase as any)
      .from('assinaturas')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!data) return null;

    // Buscar nome do pacote
    const { data: pacote } = await (supabase as any)
      .from('pacotes')
      .select('nome')
      .eq('id', data.pacote_id)
      .single();

    return assinaturaDBToAssinatura(data, pacote?.nome);
  } catch (error) {
    console.error('[assinaturasService] Erro ao buscar assinatura:', error);
    return null;
  }
}

// Criar assinatura
export async function createAssinatura(assinatura: Omit<Assinatura, 'id' | 'pacoteNome' | 'createdAt' | 'updatedAt'>): Promise<Assinatura | null> {
  try {
    const assinaturaDB = {
      conta_id: assinatura.contaId,
      pacote_id: assinatura.pacoteId,
      status: assinatura.status,
      data_inicio: assinatura.dataInicio,
      data_fim: assinatura.dataFim || null,
      valor_mensal: assinatura.valorMensal,
      forma_pagamento: assinatura.formaPagamento || null,
      proxima_cobranca: assinatura.proximaCobranca || null,
    };

    const { data, error } = await (supabase as any)
      .from('assinaturas')
      .insert(assinaturaDB)
      .select()
      .single();

    if (error) throw error;
    if (!data) return null;

    // Buscar nome do pacote
    const { data: pacote } = await (supabase as any)
      .from('pacotes')
      .select('nome')
      .eq('id', data.pacote_id)
      .single();

    return assinaturaDBToAssinatura(data, pacote?.nome);
  } catch (error) {
    console.error('[assinaturasService] Erro ao criar assinatura:', error);
    throw error;
  }
}

// Atualizar assinatura
export async function updateAssinatura(id: string, assinatura: Partial<Omit<Assinatura, 'id' | 'pacoteNome' | 'createdAt' | 'updatedAt'>>): Promise<Assinatura | null> {
  try {
    const updateData: any = {};

    if (assinatura.contaId !== undefined) updateData.conta_id = assinatura.contaId;
    if (assinatura.pacoteId !== undefined) updateData.pacote_id = assinatura.pacoteId;
    if (assinatura.status !== undefined) updateData.status = assinatura.status;
    if (assinatura.dataInicio !== undefined) updateData.data_inicio = assinatura.dataInicio;
    if (assinatura.dataFim !== undefined) updateData.data_fim = assinatura.dataFim || null;
    if (assinatura.valorMensal !== undefined) updateData.valor_mensal = assinatura.valorMensal;
    if (assinatura.formaPagamento !== undefined) updateData.forma_pagamento = assinatura.formaPagamento || null;
    if (assinatura.proximaCobranca !== undefined) updateData.proxima_cobranca = assinatura.proximaCobranca || null;

    const { data, error } = await (supabase as any)
      .from('assinaturas')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!data) return null;

    // Buscar nome do pacote
    const { data: pacote } = await (supabase as any)
      .from('pacotes')
      .select('nome')
      .eq('id', data.pacote_id)
      .single();

    return assinaturaDBToAssinatura(data, pacote?.nome);
  } catch (error) {
    console.error('[assinaturasService] Erro ao atualizar assinatura:', error);
    throw error;
  }
}

// Deletar assinatura
export async function deleteAssinatura(id: string): Promise<boolean> {
  try {
    const { error } = await (supabase as any)
      .from('assinaturas')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('[assinaturasService] Erro ao deletar assinatura:', error);
    throw error;
  }
}

